import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';

import { Box, Stack } from '@mui/system';
import {
  Card,
  Select,
  MenuItem,
  useTheme,
  CardHeader,
  Typography,
  InputLabel,
  CardContent,
  FormControl,
  FormHelperText
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import { useGetEmailMessageTemplatesQuery } from 'src/services/email-sync/email-sync.service';
import { TemplateVisibility, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import {
  useGetOrganizationSettingsQuery,
  useUpdateOrganizationSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

/**
 * Chooses the email a local applicant gets when they are rejected (JT1-1576). Local applicants have
 * no JobTetris account, so this is the only rejection they can receive, and it is sent from the job
 * owner's connected mailbox. Leaving it empty sends nothing — there is no JobTetris fallback.
 */
export default function LocalRejectionEmailSetting() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const [templateId, setTemplateId] = useState('');

  // Only org-wide templates can be used — the email is sent in the background on the job owner's
  // behalf, so a template private to one account would not be resolvable.
  const { currentData: templatesData } = useGetEmailMessageTemplatesQuery({
    page: 1,
    per_page: 100,
    type: EmailMessageTemplateType.APPLICATION_REJECTION
  });

  const orgWideTemplates = useMemo(
    () => (templatesData?.results ?? []).filter(
      (template) => template.visibility === TemplateVisibility.ORG_WIDE
    ),
    [templatesData]
  );

  const templateSetting = useMemo(
    () => settingsData?.find((setting) => setting.setting_type === OrganizationSetting.LOCAL_REJECTION_TEMPLATE_ID),
    [settingsData]
  );

  useEffect(() => {
    if (templateSetting) {
      setTemplateId((templateSetting.setting_value as string) ?? '');
    }
  }, [templateSetting]);

  const onTemplateChange = async (value: string) => {
    if (!templateSetting) return;

    setTemplateId(value);

    try {
      await updateSettings({
        organizationId: organization.id,
        settings: [{ id: templateSetting.id, value }],
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  };

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="hugeicons:mail-01"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.local_rejection_email.title')}
            </Typography>
          }
        />

        <CardContent>

          <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

            <Stack>
              <Typography variant="body2">
                {t('admin-organization.settings.local_rejection_email.description')}
              </Typography>
              <FormHelperText sx={{ mt: 0 }}>
                {t('admin-organization.settings.local_rejection_email.helper')}
              </FormHelperText>
            </Stack>

            <Stack gap={1}>
              <FormControl fullWidth size="small" disabled={!canEditSettings || !templateSetting}>
                <InputLabel id="local-rejection-template-label">
                  {t('admin-organization.settings.local_rejection_email.template_label')}
                </InputLabel>
                <Select
                  labelId="local-rejection-template-label"
                  label={t('admin-organization.settings.local_rejection_email.template_label')}
                  value={templateId}
                  onChange={(e) => onTemplateChange(e.target.value as string)}
                >
                  <MenuItem value="">
                    {t('admin-organization.settings.local_rejection_email.template_none')}
                  </MenuItem>
                  {orgWideTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormHelperText sx={{ mt: 0 }}>
                {t('admin-organization.settings.local_rejection_email.template_helper')}
              </FormHelperText>
            </Stack>

          </Stack>

        </CardContent>
      </Card>
    </Box>
  )
}
